import { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  FileText, AlertTriangle, Axe, Loader, ChevronDown, Globe, Server,
  Shield, ExternalLink,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { notesApiService } from '../../services/notesApi';
import { NoteWarning, Ban } from '../../types/player';
import { CreatorProfile } from '../CreatorProfile';
import { SkeletonLoader } from '../SkeletonLoader';

interface PlayerModerationContextProps {
  userUuid: string;
  currentBanId?: string;
  className?: string;
}

export const PlayerModerationContext = memo(({ userUuid, currentBanId, className }: PlayerModerationContextProps) => {
  const [notes, setNotes] = useState<NoteWarning[]>([]);
  const [warnings, setWarnings] = useState<NoteWarning[]>([]);
  const [bans, setBans] = useState<Ban[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!userUuid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      notesApiService.getNotesForPlayer(userUuid),
      notesApiService.getWarningsForPlayer(userUuid),
      notesApiService.getBansForUser(userUuid),
    ])
      .then(([notesRes, warningsRes, bansRes]) => {
        if (cancelled) return;
        setNotes(notesRes);
        setWarnings(warningsRes);
        setBans(bansRes);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load moderation history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [userUuid]);

  const otherBans = bans.filter(b => b.ban_uuid !== currentBanId);
  const activeBans = otherBans.filter(b => b.enforced === 1);

  if (loading) {
    return (
      <div className={cn('glass-card p-4 md:p-5', className)} aria-busy="true">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">Moderation History</h3>
          </div>
          <Loader className="w-4 h-4 animate-spin text-glass-400" aria-label="Loading moderation history" />
        </div>
        <div className="flex items-center gap-2">
          <SkeletonLoader className="h-5 w-24 rounded-full" />
          <SkeletonLoader className="h-5 w-20 rounded-full" />
          <SkeletonLoader className="h-5 w-28 rounded-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('glass-card p-4 md:p-5', className)}>
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-white">Moderation History</h3>
        </div>
        <p className="text-sm text-glass-400 mt-2">{error}</p>
      </div>
    );
  }

  return (
    <div className={cn('glass-card p-4 md:p-5', className)}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between gap-3 w-full text-left rounded"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Shield className="w-4 h-4 text-amber-400 shrink-0" />
          <h3 className="text-sm font-semibold text-white truncate">Moderation History</h3>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex flex-wrap items-center gap-1.5 text-xs justify-end">
            {warnings.length > 0 && (
              <span className="badge badge-warning">
                {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
              </span>
            )}
            {notes.length > 0 && (
              <span className="badge badge-info">
                {notes.length} note{notes.length !== 1 ? 's' : ''}
              </span>
            )}
            {otherBans.length > 0 && (
              <span className="badge badge-danger">
                {otherBans.length} other ban{otherBans.length !== 1 ? 's' : ''}
              </span>
            )}
            {warnings.length === 0 && notes.length === 0 && otherBans.length === 0 && (
              <span className="badge badge-success">Clean record</span>
            )}
          </div>
          <ChevronDown className={cn('w-4 h-4 text-glass-400 transition-transform', expanded && 'rotate-180')} />
        </div>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Warnings */}
          {warnings.length > 0 && (
            <div>
              <h4 className="eyebrow text-yellow-300 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>Warnings ({warnings.length})</span>
              </h4>
              <div className="space-y-1.5">
                {warnings.slice(0, 5).map(w => (
                  <div key={w.id} className="flex items-start gap-2 py-1.5 px-2 rounded-md border border-glass-200/10 text-xs">
                    <AlertTriangle className="w-3 h-3 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-glass-300 truncate">{w.content}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-glass-400 tabular-nums">
                        <CreatorProfile creatorId={w.creator_id} />
                        <span>{formatDistanceToNow(new Date(w.created_at), { addSuffix: true })}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {warnings.length > 5 && (
                  <p className="text-xs text-glass-400 pl-5">...and {warnings.length - 5} more</p>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {notes.length > 0 && (
            <div>
              <h4 className="eyebrow text-blue-300 mb-2 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" />
                <span>Notes ({notes.length})</span>
              </h4>
              <div className="space-y-1.5">
                {notes.slice(0, 5).map(n => (
                  <div key={n.id} className="flex items-start gap-2 py-1.5 px-2 rounded-md border border-glass-200/10 text-xs">
                    <FileText className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-glass-300 truncate">{n.content}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-glass-400 tabular-nums">
                        <CreatorProfile creatorId={n.creator_id} />
                        <span>{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {notes.length > 5 && (
                  <p className="text-xs text-glass-400 pl-5">...and {notes.length - 5} more</p>
                )}
              </div>
            </div>
          )}

          {/* Other bans */}
          {otherBans.length > 0 && (
            <div>
              <h4 className="eyebrow text-red-300 mb-2 flex items-center gap-1">
                <Axe className="w-3.5 h-3.5" />
                <span>Other Bans ({otherBans.length}, {activeBans.length} active)</span>
              </h4>
              <div className="space-y-1.5">
                {otherBans.slice(0, 5).map(b => {
                  const isBanActive = b.enforced === 1;
                  const isPerm = b.expires === null;
                  return (
                    <div key={b.ban_uuid} className={cn(
                      'flex items-start gap-2 py-1.5 px-2 rounded-md border border-glass-200/10 text-xs',
                      !isBanActive && 'opacity-60'
                    )}>
                      <Axe className={cn('w-3 h-3 mt-0.5 flex-shrink-0', isBanActive ? 'text-red-400' : 'text-glass-400')} />
                      <div className="flex-1 min-w-0">
                        <p className="text-glass-300 truncate">{b.reason}</p>
                        <div className="flex items-center gap-2 mt-0.5 text-glass-400 tabular-nums">
                          <span className={cn('font-medium', isBanActive ? 'text-red-400' : 'text-gray-400')}>
                            {isBanActive ? 'Active' : 'Inactive'}
                          </span>
                          <span>{isPerm ? 'Permanent' : `Expires ${formatDistanceToNow(new Date(b.expires! * 1000), { addSuffix: true })}`}</span>
                          <span className="capitalize flex items-center gap-0.5">
                            {b.scope === 'global' || b.scope === 'universal'
                              ? <Globe className="w-2.5 h-2.5" />
                              : <Server className="w-2.5 h-2.5" />}
                            <span>{b.scope}</span>
                          </span>
                          <span>{formatDistanceToNow(new Date(b.created * 1000), { addSuffix: true })}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {otherBans.length > 5 && (
                  <p className="text-xs text-glass-400 pl-5">...and {otherBans.length - 5} more</p>
                )}
              </div>
            </div>
          )}

          {/* Link to full profile */}
          <div className="pt-2 border-t border-glass-200/10">
            <Link to={`/player/${userUuid}`} className="text-xs text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              <span>View full player profile</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
});
